import React, { useState } from "react";
import { Box, Typography, Grid, Card, CardContent, IconButton, CardActionArea, Divider, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowCircleLeft, ArrowCircleRight } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { AllocationsCountryType, AllocationType } from "../../types";
import AllocationsBar, { AllocationsListItemCellType } from "../AllocationsBar";
import { colors, monthNames } from "../../constants";

interface AllocationsListItemProps {
  data: AllocationsCountryType;
  min: number;
  max: number;
  index?: number;
  showCurrent?: boolean;
  showFuture?: boolean;
  allocations: AllocationType[];
}

const AllocationsListItem: React.FC<AllocationsListItemProps> = ({
  data,
  min,
  max,
  showCurrent = true,
  showFuture = true,
  allocations = [],
}) => {
  const theme = useTheme();
  const useIconStyles = makeStyles(() => ({
    root: {
      [theme.breakpoints.up("md")]: {
        width: theme.spacing(24),
        height: theme.spacing(24),
      },
    },
  }));

  const iconStyles = useIconStyles();
  const [current, setCurrent] = useState(1);
  const past = allocations.filter(a => a.future < 1 && showCurrent);
  const future = allocations.filter(a => a.future > 0 && showFuture);

  let marker = min;

  const cellsPast: AllocationsListItemCellType[] = [];

  past.forEach((allocation, index) => {
    const start = Math.max(allocation.band_start || min, min);
    const end = Math.min(allocation.band_end || max, max);

    if (marker < start) {
      cellsPast.push({
        start: marker,
        end: start,
        title: "No data",
      });
    }

    cellsPast.push({
      start,
      end,
      title: allocation.operators?.length ? allocation.operators.map(operator => operator.title).join(", ") : "--",
      label: allocation.operators?.length ? (
        <>
          {allocation.operators.map((operator, index, arr) => (
            <React.Fragment key={index}>
              <Link href={`/operators/operator/${operator.id}`}>{operator.title}</Link>
              {index < arr.length - 1 && <>, </>}
            </React.Fragment>
          ))}
        </>
      ) : undefined,
      data: allocation,
    });

    marker = end;

    if (end < max && index === past.length - 1) {
      cellsPast.push({
        start: end,
        end: max,
        title: "No data",
      });
    }
  }, []);

  marker = min;

  const cellsFuture: AllocationsListItemCellType[] = [];

  future.forEach((allocation, index) => {
    const start = Math.max(allocation.band_start || min, min);
    const end = Math.min(allocation.band_end || max, max);

    if (marker < start) {
      cellsFuture.push({
        start: marker,
        end: start,
        title: "No data",
      });
    }

    cellsFuture.push({
      start,
      end,
      title: allocation.operators?.length ? allocation.operators.map(operator => operator.title).join(", ") : "--",
      label: allocation.operators?.length ? (
        <>
          {allocation.operators.map((operator, index, arr) => (
            <React.Fragment key={index}>
              <Link href={`/operators/operator/${operator.id}`}>{operator.title}</Link>
              {index < arr.length - 1 && <>, </>}
            </React.Fragment>
          ))}
        </>
      ) : undefined,
      data: allocation,
    });

    marker = end;

    if (end < max && index === future.length - 1) {
      cellsFuture.push({
        start: end,
        end: max,
        title: "No data",
      });
    }
  }, []);

  const cells = [...cellsPast, ...cellsFuture];
  const cell = cells[current];
  const total = cells.length;

  return (
    <>
      {data && (
        <>
          <Box paddingY={8} width="100%">
            <Typography variant="h4" style={{ marginTop: "15px", marginBottom: "15px" }}>
              <span
                className={`fi fi-${data.code?.toLowerCase() ?? ""}`}
                style={{ width: "30px", height: "23px", border: "1px solid #808080", margin: "0px 7px 0px 0" }}
              ></span>{" "}
              {data.title}
            </Typography>
            {total > 0 ? (
              <Box paddingTop={12} width="100%">
                {showCurrent && <AllocationsBar data={cellsPast} current={current} min={min} max={max} setCurrent={setCurrent} />}
                {showFuture && (
                  <AllocationsBar
                    data={cellsFuture}
                    current={current}
                    min={min}
                    max={max}
                    setCurrent={setCurrent}
                    startIndex={cellsPast.length}
                    isFuture
                  />
                )}
                {cell && (
                  <Grid container justifyContent="space-evenly" alignItems="top">
                    <Grid>
                      <IconButton
                        style={{ marginTop: "20px" }}
                        classes={iconStyles}
                        disabled={total < 2}
                        onClick={() => {
                          setCurrent(state => (state + total - 1) % total);
                        }}
                        size="large"
                      >
                        <ArrowCircleLeft style={{ fontSize: 40, color: "#24b654" }} />
                      </IconButton>
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                      <Card>
                        <CardActionArea>
                          <CardContent>
                            <Box>
                              <Grid container spacing={1}>
                                {cell.title === "--" ? (
                                  <>
                                    <Grid size={{ xs: 12 }}>
                                      <Typography align="center">
                                        <br />
                                        <br />
                                        <br />
                                        <strong>Assigned</strong>
                                        <br />
                                        <br />
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                      <Typography align="right" style={{ paddingRight: "10px" }}>
                                        <strong>Frequency range:</strong>
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                      <Typography>
                                        {cell.start} MHz - {cell.end} MHz
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                      <Typography align="center">
                                        <br />
                                        <br />
                                        <br />
                                      </Typography>
                                    </Grid>
                                  </>
                                ) : (
                                  <>
                                    <Grid size={{ xs: 4 }}>
                                      <Typography align="right" style={{ paddingRight: "10px" }}>
                                        <strong>Operator:</strong>
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 8 }}>
                                      <Grid container alignItems="center" spacing={1}>
                                        <Grid>
                                          <Box
                                            width={12}
                                            height={12}
                                            bgcolor={
                                              //@ts-ignore
                                              cell?.data?.operators?.length > 1
                                                ? "#000000"
                                                : cell?.data?.assigned !== "Y"
                                                  ? "#ffffff"
                                                  : (() => {
                                                      const legends = Array.from(
                                                        new Set(
                                                          cells
                                                            .filter(cell => (cell.data?.operators?.length ?? 0) > 0)
                                                            //@ts-ignore
                                                            .map(cell => (cell.data?.operators?.length > 1 ? "Multiple" : cell.title)),
                                                        ),
                                                      );
                                                      const colorIndex = legends.findIndex(legend => legend === cell.title);
                                                      return colors[colorIndex > -1 ? colorIndex : legends.length];
                                                    })()
                                            }
                                            border="1px solid #cccccc"
                                            style={{ marginRight: "5px" }}
                                          />
                                        </Grid>
                                        <Grid>
                                          <Typography>{cell.label || cell.title}</Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>

                                    <Grid size={{ xs: 4 }}>
                                      <Typography align="right" style={{ paddingRight: "10px" }}>
                                        <strong>Frequency range:</strong>
                                      </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 8 }}>
                                      <Typography>
                                        {cell.start} MHz - {cell.end} MHz
                                      </Typography>
                                    </Grid>
                                    {!!cell.data && (
                                      <>
                                        {cell.data.geographical_title && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Geography range:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>{cell.data.geographical_title}</Typography>
                                            </Grid>
                                          </>
                                        )}
                                        {/*                                     {cell.data && (
                                      <>
                                        <Grid size={{ xs: 6 }}>
                                          <Typography align="right" style={{paddingRight:"10px"}}>
                                            <strong>Assigned:</strong>
                                          </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                          <Typography>{cell.data?.assigned === "Y" ? "Yes" : "No"}</Typography>
                                        </Grid>
                                      </>
                                    )} */}
                                        {cell.data?.usages && cell.data.usages.length > 0 && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Usage:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>{cell.data.usages.map(usage => usage.title).join(", ")}</Typography>
                                            </Grid>
                                          </>
                                        )}
                                        {cell.data?.format_title && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Spectrum format:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>{cell.data.format_title}</Typography>
                                            </Grid>
                                          </>
                                        )}
                                        {cell.data?.award_year && parseInt(cell.data.award_year) > 0 && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Awarded:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>
                                                {cell.data.award_month && parseInt(cell.data.award_month) > 0
                                                  ? monthNames[parseInt(cell.data.award_month) - 1]
                                                  : ""}{" "}
                                                {cell.data.award_year}
                                              </Typography>
                                            </Grid>
                                          </>
                                        )}
                                        {cell.data?.plan_notes && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Notes:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>{cell.data.plan_notes}</Typography>
                                            </Grid>
                                          </>
                                        )}
                                        {cell.data?.assignment_title && (
                                          <>
                                            <Grid size={{ xs: 4 }}>
                                              <Typography align="right" style={{ paddingRight: "10px" }}>
                                                <strong>Assignment:</strong>
                                              </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 8 }}>
                                              <Typography>{cell.data.assignment_title}</Typography>
                                            </Grid>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </Grid>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                    <Grid>
                      <IconButton
                        style={{ marginTop: "20px" }}
                        classes={iconStyles}
                        disabled={total < 2}
                        onClick={() => {
                          setCurrent(state => (state + 1) % total);
                        }}
                        size="large"
                      >
                        <ArrowCircleRight style={{ fontSize: 40, color: "#24b654" }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
              </Box>
            ) : (
              <Box paddingTop={4} width="100%">
                <Typography>No data.</Typography>
              </Box>
            )}
          </Box>
          <Divider />
        </>
      )}
    </>
  );
};

export default AllocationsListItem;
